// /investigations — lista de investigaciones + crear nueva

import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaPlus,
  FaSearch,
  FaArchive,
  FaCheckCircle,
  FaPencilAlt,
  FaProjectDiagram,
  FaTimes,
  FaUser,
  FaRobot,
  FaPaperPlane,
  FaUsers,
  FaLink,
  FaLightbulb,
  FaFileAlt,
  FaPlay,
  FaSortAmountDown,
  FaTag,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechButton,
  TechInput,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import GlobalNetwork from './GlobalNetwork';
import PoliticiansDirectory from './PoliticiansDirectory';
import CronCountdown from '../common/CronCountdown';
import {
  listInvestigations,
  createInvestigation,
  wizardChat,
} from '../../services/investigationService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 1rem;
`;

const Card = styled(Link)`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  padding: 1rem 1.1rem;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  color: inherit;
  position: relative;
  transition: all 0.18s ease;
  border-left: 4px solid ${({ $color }) => $color || accent};
  min-height: 200px;

  &:hover {
    border-color: rgba(0, 180, 216, 0.5);
    background: rgba(0, 180, 216, 0.04);
    transform: translateY(-1px);
  }

  .title {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 1rem;
    line-height: 1.3;
    padding-right: ${({ $hasActive }) => ($hasActive ? '72px' : '0')};
  }
  .hyp {
    color: var(--text-secondary);
    font-size: 0.83rem;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .live-pill {
    position: absolute;
    top: 12px;
    right: 12px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 999px;
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #62d59a;
    background: rgba(98, 213, 154, 0.08);
    border: 1px solid rgba(98, 213, 154, 0.4);
    .dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: #62d59a;
      box-shadow: 0 0 0 0 rgba(98, 213, 154, 0.6);
      animation: livePulse 1.6s infinite;
    }
    @keyframes livePulse {
      0%   { box-shadow: 0 0 0 0 rgba(98, 213, 154, 0.55); }
      70%  { box-shadow: 0 0 0 8px rgba(98, 213, 154, 0); }
      100% { box-shadow: 0 0 0 0 rgba(98, 213, 154, 0); }
    }
  }
  .stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.3rem;
    padding: 0.5rem 0;
    border-top: 1px dashed ${borderSoft};
    border-bottom: 1px dashed ${borderSoft};
  }
  .stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    .icon {
      color: var(--text-disabled);
      font-size: 0.8rem;
    }
    .val {
      font-family: ${mono};
      color: var(--text-primary);
      font-weight: 700;
      font-size: 0.95rem;
      line-height: 1;
    }
    .lbl {
      font-family: ${mono};
      color: var(--text-disabled);
      font-size: 0.54rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    &.has-val .icon { color: ${accent}; }
  }
  .meta {
    margin-top: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    align-items: center;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
  .status {
    padding: 2px 7px;
    border-radius: 4px;
    border: 1px solid ${borderSoft};
  }
  .status-active { background: rgba(6, 214, 160, 0.08); color: var(--alert-success); border-color: rgba(6, 214, 160, 0.3); }
  .status-archived { background: rgba(244, 162, 97, 0.08); color: var(--alert-warning, #f4a261); border-color: rgba(244, 162, 97, 0.3); }
  .status-completed { background: rgba(0, 180, 216, 0.08); color: ${accent}; border-color: rgba(0, 180, 216, 0.3); }
`;

const OverviewStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.6rem;
  margin-bottom: 1rem;

  .cell {
    background: ${panelDeep};
    border: 1px solid ${borderSoft};
    border-radius: 8px;
    padding: 0.7rem 0.85rem;
    display: flex;
    flex-direction: column;
    gap: 4px;
    .lbl {
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
    .val {
      color: var(--text-primary);
      font-weight: 700;
      font-size: 1.45rem;
      line-height: 1;
    }
    .sub {
      color: var(--text-disabled);
      font-size: 0.72rem;
    }
    &.live .val { color: #62d59a; }
  }
`;

const SortDropdown = styled.select`
  background: rgba(11, 19, 32, 0.65);
  border: 1px solid ${borderSoft};
  border-radius: 999px;
  color: var(--text-secondary);
  padding: 0.4rem 2.2rem 0.4rem 0.9rem;
  font-size: 0.78rem;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--text-disabled) 50%),
    linear-gradient(135deg, var(--text-disabled) 50%, transparent 50%);
  background-position: calc(100% - 14px) 50%, calc(100% - 9px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  &:focus { outline: none; border-color: rgba(0, 180, 216, 0.4); }
`;

const TopBar = styled.div`
  display: flex;
  gap: 0.65rem;
  align-items: center;
  margin-bottom: 1.2rem;
  flex-wrap: wrap;
`;

const FilterChip = styled.button`
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.15)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.4)' : borderSoft)};
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.15s ease;
  &:hover { color: var(--text-primary); }
`;

const ModalBackdrop = styled.div`
  position: fixed; inset: 0; z-index: 1400;
  background: rgba(8, 14, 24, 0.7);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: 0.5rem;
  overflow-y: auto;
`;
const ModalCard = styled.div`
  width: min(720px, 100%);
  max-height: calc(100vh - 1rem);
  display: flex;
  flex-direction: column;
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 12px;
  padding: 1rem 1.1rem;
  position: relative;
  overflow: hidden;

  @media (min-width: 720px) {
    padding: 1.4rem 1.5rem;
  }

  h2 { color: var(--text-primary); margin: 0 0 0.85rem; }
  label {
    display: block;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin: 0.7rem 0 0.3rem;
  }
  textarea {
    width: 100%;
    min-height: 80px;
    background: rgba(11, 19, 32, 0.65);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    color: var(--text-primary);
    font-family: inherit;
    padding: 0.55rem 0.75rem;
    font-size: 0.88rem;
    outline: none;
    resize: vertical;
    &:focus { border-color: rgba(0, 180, 216, 0.5); }
  }
  .close {
    position: absolute; top: 12px; right: 14px;
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    &:hover { color: var(--alert-error); }
  }
  .actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.1rem; }
`;

const COVERS = ['#00b4d8', '#06d6a0', '#f4a261', '#ef476f', '#a78bfa', '#fbbf24'];

const SCOPE_OPTIONS = [
  { value: 'local', label: 'Local · San Nicolás' },
  { value: 'provincial', label: 'Provincial · Buenos Aires' },
  { value: 'national', label: 'Nacional · Argentina' },
];

const TEMPLATES = [
  {
    title: 'Red de poder Passaglia',
    hypothesis: 'Mapear los hermanos Passaglia (Manuel y Santiago) y sus vínculos comerciales, políticos y familiares en San Nicolás y BA.',
    region: 'San Nicolás de los Arroyos',
    scope: 'local',
    cover_color: '#ef476f',
    tags: ['passaglia', 'familia', 'red-poder'],
  },
  {
    title: 'Bloque LLA en el HCD San Nicolás',
    hypothesis: 'Identificar a los concejales de La Libertad Avanza en SN, sus alianzas, votaciones diferenciales y red de operadores.',
    region: 'San Nicolás de los Arroyos',
    scope: 'local',
    cover_color: '#a78bfa',
    tags: ['lla', 'bloque', 'hcd'],
  },
  {
    title: 'Concejales nicoleños activos · 2024-2026',
    hypothesis: 'Para cada concejal del HCD: trayectoria, votaciones, conexiones empresariales, fortalezas y debilidades accionables.',
    region: 'San Nicolás de los Arroyos',
    scope: 'local',
    cover_color: '#00b4d8',
    tags: ['hcd', 'concejales', 'inteligencia-politica'],
  },
  {
    title: 'Vínculos provinciales del oficialismo nicoleño',
    hypothesis: 'Mapear cómo el gobierno municipal SN se relaciona con La Plata (PRO/JxC bonaerense) y Casa Rosada (LLA nacional).',
    region: 'San Nicolás de los Arroyos',
    scope: 'provincial',
    cover_color: '#f4a261',
    tags: ['multinivel', 'pba', 'nacional'],
  },
];

function NewInvestigationModal({ onClose, onCreated }) {
  const [messages, setMessages] = useState([]); // {role, content}
  const [proposal, setProposal] = useState(null);
  const [ready, setReady] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState(null);
  const bodyRef = React.useRef(null);

  // Mensaje inicial automático del asistente
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await wizardChat({ messages: [] });
        if (cancelled) return;
        if (r?.answer) {
          setMessages([{ role: 'assistant', content: r.answer }]);
        }
      } catch (e) {
        // si falla, dejamos un saludo local
        setMessages([{ role: 'assistant', content: 'Hola, contame qué querés investigar.' }]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, sending, proposal]);

  const send = async (override) => {
    const q = (override ?? input).trim();
    if (!q || sending) return;
    setErr(null);
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const r = await wizardChat({ messages: next });
      setMessages((prev) => [...prev, { role: 'assistant', content: r.answer || '(sin respuesta)' }]);
      if (r.proposal) setProposal(r.proposal);
      if (r.ready_to_create) setReady(true);
      else setReady(false);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSending(false);
    }
  };

  const confirmCreate = async () => {
    if (!proposal) return;
    setCreating(true); setErr(null);
    try {
      const r = await wizardChat({ confirm: true, proposal, messages });
      if (r?.investigation) {
        onCreated(r.investigation);
      } else {
        setErr('No se pudo crear');
      }
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setCreating(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose}><FaTimes /></button>
        <h2 style={{ flexShrink: 0 }}><FaProjectDiagram style={{ marginRight: 8, color: accent }} /> Nueva investigación · asistente IA</h2>
        <p style={{ color: 'var(--text-disabled)', fontSize: '0.82rem', marginBottom: '0.7rem', flexShrink: 0 }}>
          Contale qué querés investigar. La IA propone título, hipótesis y entidades iniciales.
        </p>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: 4 }}>

        {/* Atajos a plantillas: agregan un mensaje del usuario al chat */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {TEMPLATES.map((t) => (
            <button
              key={t.title}
              type="button"
              onClick={() => send(`Usar plantilla: ${t.title} — ${t.hypothesis}`)}
              disabled={sending}
              style={{
                background: 'transparent',
                border: '1px solid ' + borderSoft,
                color: 'var(--text-secondary)',
                padding: '5px 10px',
                borderRadius: 999,
                fontSize: '0.72rem',
                cursor: 'pointer',
              }}
            >
              ⇨ {t.title}
            </button>
          ))}
        </div>

        <div
          ref={bodyRef}
          style={{
            background: 'rgba(11, 19, 32, 0.5)',
            border: '1px solid ' + borderSoft,
            borderRadius: 8,
            padding: '0.7rem 0.85rem',
            maxHeight: 240,
            minHeight: 160,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: m.role === 'user' ? 'rgba(0, 180, 216, 0.1)' : 'rgba(6, 214, 160, 0.1)',
                border: '1px solid ' + borderSoft,
                color: m.role === 'user' ? accent : 'var(--alert-success)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', flexShrink: 0,
              }}>
                {m.role === 'user' ? <FaUser /> : <FaRobot />}
              </div>
              <div style={{
                flex: 1, minWidth: 0,
                background: 'rgba(11, 19, 32, 0.7)',
                border: '1px solid ' + borderSoft,
                borderRadius: 6,
                padding: '0.5rem 0.7rem',
                color: 'var(--text-secondary)',
                fontSize: '0.85rem',
                lineHeight: 1.5,
              }}>
                {m.role === 'assistant' ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                ) : m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div style={{ color: 'var(--text-disabled)', fontFamily: mono, fontSize: '0.74rem', paddingLeft: 32 }}>
              pensando…
            </div>
          )}
        </div>

        {proposal && (
          <div style={{
            marginTop: 12,
            background: 'rgba(0, 180, 216, 0.05)',
            border: '1px solid rgba(0, 180, 216, 0.3)',
            borderRadius: 8,
            padding: '0.85rem 1rem',
          }}>
            <div style={{
              fontFamily: mono, fontSize: '0.6rem', letterSpacing: '0.18em',
              textTransform: 'uppercase', color: accent, marginBottom: 6,
            }}>
              Propuesta del asistente
            </div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.95rem', marginBottom: 4 }}>
              {proposal.title}
            </div>
            {proposal.hypothesis && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 6 }}>
                {proposal.hypothesis}
              </div>
            )}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6,
              fontFamily: mono, fontSize: '0.6rem',
              letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--text-disabled)',
            }}>
              <span>📍 {proposal.region || 'SN'}</span>
              <span>scope: {proposal.scope || 'local'}</span>
              {proposal.cover_color && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  color:
                  <span style={{ width: 14, height: 14, borderRadius: 3, background: proposal.cover_color, display: 'inline-block' }} />
                </span>
              )}
              {Array.isArray(proposal.tags) && proposal.tags.length > 0 && (
                <span>tags: {proposal.tags.join(', ')}</span>
              )}
            </div>
            {Array.isArray(proposal.suggested_entities) && proposal.suggested_entities.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{
                  fontFamily: mono, fontSize: '0.58rem',
                  letterSpacing: '0.16em', textTransform: 'uppercase',
                  color: 'var(--text-disabled)', marginBottom: 4,
                }}>
                  Entidades iniciales sugeridas ({proposal.suggested_entities.length})
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: '0.83rem' }}>
                  {proposal.suggested_entities.slice(0, 6).map((s, i) => (
                    <li key={i}>
                      <strong>{s.name}</strong> ({s.type}){s.summary ? ' — ' + s.summary : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {err && <ErrorBox style={{ marginTop: 12 }}>{err}</ErrorBox>}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={proposal
              ? 'Pedile ajustes ("achicalo a SN solo", "agregá a Manuel Passaglia"…) o confirmá.'
              : 'Contame qué querés investigar (Enter = enviar · Shift+Enter = salto)'}
            disabled={sending || creating}
            style={{
              flex: 1, minHeight: 50, maxHeight: 120, resize: 'vertical',
              background: 'rgba(11, 19, 32, 0.65)',
              border: '1px solid ' + borderSoft,
              borderRadius: 7,
              color: 'var(--text-primary)',
              fontFamily: 'inherit', padding: '0.55rem 0.75rem',
              fontSize: '0.88rem', outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={sending || creating || !input.trim()}
            style={{
              flexShrink: 0, width: 44, height: 44, borderRadius: 8,
              border: '1px solid rgba(0, 180, 216, 0.35)',
              background: 'rgba(0, 180, 216, 0.15)',
              color: accent, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: (sending || !input.trim()) ? 0.45 : 1,
            }}
            aria-label="Enviar"
          >
            <FaPaperPlane />
          </button>
        </div>
        </div>{/* /scroll-area */}

        <div
          className="actions"
          style={{
            display: 'flex', justifyContent: 'space-between',
            gap: 8, marginTop: 12, flexShrink: 0, flexWrap: 'wrap',
            borderTop: '1px solid ' + borderSoft, paddingTop: 12,
          }}
        >
          <TechButton
            onClick={onClose}
            disabled={creating}
            style={{ background: 'transparent', color: 'var(--text-secondary)' }}
          >
            Cancelar
          </TechButton>
          <TechButton
            onClick={confirmCreate}
            disabled={!proposal?.title || creating}
            title={!proposal ? 'Charlá primero con el asistente' : ''}
          >
            {creating
              ? 'Creando…'
              : proposal?.title
                ? (ready ? '✓ Crear investigación' : 'Crear ahora')
                : 'Esperando propuesta…'}
          </TechButton>
        </div>
      </ModalCard>
    </ModalBackdrop>
  );
}

const SORT_OPTIONS = [
  { value: 'updated', label: 'Actualización reciente' },
  { value: 'activity', label: 'Actividad reciente' },
  { value: 'entities', label: 'Más entidades' },
  { value: 'reports', label: 'Más informes' },
  { value: 'theories', label: 'Más teorías' },
  { value: 'alpha', label: 'Alfabético' },
];

function formatRelative(isoOrNull) {
  if (!isoOrNull) return '—';
  const ms = Date.now() - new Date(isoOrNull).getTime();
  if (ms < 60_000) return 'hace segundos';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `hace ${days} d`;
  return new Date(isoOrNull).toLocaleDateString('es-AR');
}

const VIEW_TABS = [
  { id: 'dossiers', label: 'Dossiers' },
  { id: 'politicians', label: 'Políticos nicoleños' },
  { id: 'network', label: 'Red global · SN' },
];

export default function InvestigationList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState(null);
  const [sortBy, setSortBy] = useState('updated');
  const [query, setQuery] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('tab') || 'dossiers');
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      setErr(null);
      const r = await listInvestigations();
      setItems(r);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Refrescamos automáticamente cuando alguna investigación tiene run activo
  useEffect(() => {
    const hasActive = items.some((it) => it.active_run_id);
    if (!hasActive) return undefined;
    const t = setInterval(() => { load(); }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.some((it) => it.active_run_id)]);

  // Lista de tags únicos (top 12 por frecuencia)
  const allTags = React.useMemo(() => {
    const counts = new Map();
    for (const it of items) {
      for (const t of (Array.isArray(it.tags) ? it.tags : [])) {
        const k = String(t).trim();
        if (!k) continue;
        counts.set(k, (counts.get(k) || 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
  }, [items]);

  const filtered = items.filter((it) => {
    if (filter === 'active' && it.status !== 'active') return false;
    if (filter === 'archived' && it.status !== 'archived') return false;
    if (filter === 'completed' && it.status !== 'completed') return false;
    if (filter === 'live' && !it.active_run_id) return false;
    if (tagFilter && !((it.tags || []).map((t) => String(t)).includes(tagFilter))) return false;
    if (query) {
      const q = query.toLowerCase();
      return (
        (it.title || '').toLowerCase().includes(q) ||
        (it.hypothesis || '').toLowerCase().includes(q) ||
        (it.tags || []).some((t) => String(t).toLowerCase().includes(q))
      );
    }
    return true;
  });

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    if (sortBy === 'updated') arr.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    else if (sortBy === 'activity') arr.sort((a, b) => new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0));
    else if (sortBy === 'entities') arr.sort((a, b) => (b.entities_count || 0) - (a.entities_count || 0));
    else if (sortBy === 'reports') arr.sort((a, b) => (b.reports_count || 0) - (a.reports_count || 0));
    else if (sortBy === 'theories') arr.sort((a, b) => (b.theories_count || 0) - (a.theories_count || 0));
    else if (sortBy === 'alpha') arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    return arr;
  }, [filtered, sortBy]);

  // Totales agregados
  const totals = items.reduce((acc, it) => {
    acc.entities += it.entities_count || 0;
    acc.connections += it.connections_count || 0;
    acc.theories += it.theories_count || 0;
    acc.reports += it.reports_count || 0;
    if (it.active_run_id) acc.live += 1;
    if (it.status === 'active') acc.active += 1;
    return acc;
  }, { entities: 0, connections: 0, theories: 0, reports: 0, live: 0, active: 0 });

  return (
    <TechShell>
      <TechPageHeader
        title="Dossiers · Política nicoleña"
        subtitle="Investigaciones con redes de contactos, perfiles, conexiones, teorías y agentes IA. Cada dossier acumula entidades, hipótesis, evidencia y reportes ejecutables en autopilot."
        dossier="DOSSIERS"
        actions={
          <TechButton onClick={() => setShowNew(true)}>
            <FaPlus style={{ marginRight: 6 }} /> Nuevo dossier
          </TechButton>
        }
      >
        <CronCountdown cronKey="politicians-news" label="Próximas noticias en" />
      </TechPageHeader>

      <TechTabs tabs={VIEW_TABS} active={view} onChange={setView} />

      {view === 'network' && <GlobalNetwork />}
      {view === 'politicians' && <PoliticiansDirectory />}

      {view === 'dossiers' && !loading && items.length > 0 && (
        <OverviewStats>
          <div className="cell">
            <div className="lbl">Dossiers</div>
            <div className="val">{items.length}</div>
            <div className="sub">{totals.active} activos · {items.length - totals.active} archivados/completos</div>
          </div>
          {totals.live > 0 && (
            <div className="cell live">
              <div className="lbl">En vivo</div>
              <div className="val">{totals.live}</div>
              <div className="sub">autopilot corriendo</div>
            </div>
          )}
          <div className="cell">
            <div className="lbl">Entidades</div>
            <div className="val">{totals.entities}</div>
            <div className="sub">indexadas · personas, empresas, lugares</div>
          </div>
          <div className="cell">
            <div className="lbl">Conexiones</div>
            <div className="val">{totals.connections}</div>
            <div className="sub">edges entre entidades</div>
          </div>
          <div className="cell">
            <div className="lbl">Hipótesis</div>
            <div className="val">{totals.theories}</div>
            <div className="sub">teorías evaluadas con args</div>
          </div>
          <div className="cell">
            <div className="lbl">Informes</div>
            <div className="val">{totals.reports}</div>
            <div className="sub">ejecutivos generados por IA</div>
          </div>
        </OverviewStats>
      )}

      {view === 'dossiers' && (<>
      <TopBar>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)', fontSize: '0.82rem' }} />
          <TechInput
            placeholder="Buscar por título, hipótesis o tag…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
        <FilterChip $active={filter === 'all'} onClick={() => setFilter('all')}>
          Todas <span style={{ opacity: 0.6, marginLeft: 4 }}>· {items.length}</span>
        </FilterChip>
        {totals.live > 0 && (
          <FilterChip
            $active={filter === 'live'}
            onClick={() => setFilter('live')}
            style={{ color: '#62d59a', borderColor: 'rgba(98, 213, 154, 0.4)' }}
          >
            <FaPlay size={9} /> En vivo <span style={{ opacity: 0.7, marginLeft: 4 }}>· {totals.live}</span>
          </FilterChip>
        )}
        <FilterChip $active={filter === 'active'} onClick={() => setFilter('active')}>
          <FaPencilAlt size={11} /> Activas
        </FilterChip>
        <FilterChip $active={filter === 'completed'} onClick={() => setFilter('completed')}>
          <FaCheckCircle size={11} /> Completadas
        </FilterChip>
        <FilterChip $active={filter === 'archived'} onClick={() => setFilter('archived')}>
          <FaArchive size={11} /> Archivadas
        </FilterChip>
        <SortDropdown value={sortBy} onChange={(e) => setSortBy(e.target.value)} title="Ordenar por">
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </SortDropdown>
      </TopBar>

      {allTags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
          <span style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
            <FaTag style={{ marginRight: 4 }} /> Tags
          </span>
          {tagFilter && (
            <FilterChip $active onClick={() => setTagFilter(null)} style={{ borderStyle: 'dashed' }}>
              <FaTimes size={9} /> {tagFilter}
            </FilterChip>
          )}
          {allTags.filter(([t]) => t !== tagFilter).map(([tag, count]) => (
            <FilterChip
              key={tag}
              $active={false}
              onClick={() => setTagFilter(tag)}
              style={{ fontSize: '0.7rem' }}
            >
              {tag} <span style={{ opacity: 0.55, marginLeft: 4 }}>{count}</span>
            </FilterChip>
          ))}
        </div>
      )}

      <TechSection title={`${sorted.length} ${sorted.length === 1 ? 'investigación' : 'investigaciones'}${tagFilter ? ` · tag: ${tagFilter}` : ''}`}>
        {loading ? <Loading>Cargando…</Loading>
          : err ? <ErrorBox>{err}</ErrorBox>
          : sorted.length === 0 ? (
            <Empty>
              {items.length === 0 ? (
                <div>
                  Aún no creaste ningún dossier. Empezá con <strong>"Nuevo dossier"</strong> arriba a la derecha — el asistente IA te ayuda a definir título, hipótesis y entidades iniciales.
                </div>
              ) : 'Sin resultados con ese filtro. Probá limpiar la búsqueda o el tag activo.'}
            </Empty>
          ) : (
            <Grid>
              {sorted.map((it) => {
                const totalLoad = (it.entities_count || 0) + (it.connections_count || 0) + (it.reports_count || 0) + (it.theories_count || 0);
                return (
                  <Card
                    key={it.id}
                    to={`/investigations/${it.slug}`}
                    $color={it.cover_color}
                    $hasActive={!!it.active_run_id}
                  >
                    {it.active_run_id && (
                      <div className="live-pill" title="Autopilot corriendo">
                        <span className="dot" /> LIVE
                      </div>
                    )}
                    <div className="title">{it.title}</div>
                    {it.hypothesis && <div className="hyp">{it.hypothesis}</div>}

                    <div className="stats">
                      <div className={`stat${it.entities_count ? ' has-val' : ''}`}>
                        <FaUsers className="icon" />
                        <div className="val">{it.entities_count || 0}</div>
                        <div className="lbl">Entidades</div>
                      </div>
                      <div className={`stat${it.connections_count ? ' has-val' : ''}`}>
                        <FaLink className="icon" />
                        <div className="val">{it.connections_count || 0}</div>
                        <div className="lbl">Conex.</div>
                      </div>
                      <div className={`stat${it.theories_count ? ' has-val' : ''}`}>
                        <FaLightbulb className="icon" />
                        <div className="val">{it.theories_count || 0}</div>
                        <div className="lbl">Teorías</div>
                      </div>
                      <div className={`stat${it.reports_count ? ' has-val' : ''}`}>
                        <FaFileAlt className="icon" />
                        <div className="val">{it.reports_count || 0}</div>
                        <div className="lbl">Informes</div>
                      </div>
                    </div>

                    <div className="meta">
                      <span className={`status status-${it.status}`}>{it.status}</span>
                      {it.scope && <span>{it.scope}</span>}
                      <span title={it.last_activity_at ? `Última actividad: ${new Date(it.last_activity_at).toLocaleString('es-AR')}` : 'Sin actividad'}>
                        {totalLoad === 0
                          ? 'sin actividad'
                          : `activ. ${formatRelative(it.last_activity_at || it.updated_at)}`}
                      </span>
                      {Array.isArray(it.tags) && it.tags.slice(0, 3).map((t) => (
                        <TechChip
                          key={t}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTagFilter(t); }}
                          style={{ cursor: 'pointer' }}
                        >
                          {t}
                        </TechChip>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </Grid>
          )
        }
      </TechSection>

      </>)}

      {showNew && (
        <NewInvestigationModal
          onClose={() => setShowNew(false)}
          onCreated={(inv) => {
            setShowNew(false);
            navigate(`/investigations/${inv.slug}`);
          }}
        />
      )}
    </TechShell>
  );
}
